import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiQuery, ApiQuerySortTypes } from '@app/core/http/api-query';
import { Type } from '@app/settings/types/types.model';
import { TypesService } from '@app/settings/types/types.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';

interface NightlyGuestNames {
  date: string;
  room_slots: Array<{
    room_number?: number;
    guest_names: Array<string | null>;
  }>;
}

@Component({
  selector: 'app-sale-guest-names-form',
  templateUrl: './sale-guest-names-form.component.html',
  styleUrls: ['./sale-guest-names-form.component.scss'],
})
export class SaleGuestNamesFormComponent implements OnInit {
  @Input() request: any;
  @Input() requestNumber = 1;

  form: FormGroup;
  guestNamesCount = 0;
  typeLabel = '—';

  constructor(public modal: NgbActiveModal, private formBuilder: FormBuilder, private typesService: TypesService) {}
  ngOnInit(): void {
    this.guestNamesCount = Math.max(0, Number(this.request?.people || 0) * Number(this.request?.count || 0));

    this.form = this.formBuilder.group({
      nights: new FormArray([]),
    });

    this.loadTypeLabel();
    this.buildNightRows();
  }

  private loadTypeLabel(): void {
    const typeId = this.request?.type_id;
    const requestType = this.request?.type;

    if (requestType && String(requestType.id) === String(typeId) && requestType.value) {
      this.typeLabel = requestType.value;
      return;
    }

    if (!typeId) {
      return;
    }

    const query = new ApiQuery();

    query.addFilter('active', true);
    query.setSort('order_index', ApiQuerySortTypes.ASC);

    this.typesService
      .index(
        query
          .addFilters({
            type: 'rooms',
          })
          .setLimit(9999)
      )
      .subscribe((response: PaginatedResponse) => {
        const selectedType = response.data.find((type: Type) => String(type.id) === String(typeId));

        this.typeLabel = selectedType?.value || `#${typeId}`;
      });
  }

  get nights(): FormArray {
    return this.form.get('nights') as FormArray;
  }

  getGuestNames(night: FormGroup): FormArray {
    return night.get('guest_names') as FormArray;
  }

  get checkIn(): string {
    return this.formatDisplayDate(this.request?.check_in);
  }

  get checkOut(): string {
    return this.formatDisplayDate(this.request?.check_out);
  }

  get mealPlanLabel(): string {
    return this.getRelationLabel(this.request?.meal_plan, this.request?.meal_plan_id);
  }

  get statusLabel(): string {
    return this.getRelationLabel(this.request?.status, this.request?.status_id);
  }

  get hotelStarsLabel(): string {
    return this.request?.hotel_stars ? `${this.request.hotel_stars} ★` : '—';
  }

  get hotelLabel(): string {
    return this.request?.hotel_text || this.request?.hotel?.name || '—';
  }

  get budgetLabel(): string {
    const labels: { [key: string]: string } = {
      up_to_150: 'Up to €150',
      '150_250': '€150–€250',
      '250_300': '€250–€300',
      free_text: 'Free-text',
    };
    const values = (Array.isArray(this.request?.budget_options) ? this.request.budget_options : [])
      .filter((option: string) => !!labels[option])
      .map((option: string) => labels[option]);

    if (this.request?.custom_budget) {
      values.push(this.request.custom_budget);
    }

    return values.join(', ') || this.request?.budget || '—';
  }

  save(): void {
    const roomCount = Math.max(1, Number(this.request?.count || 1));
    const peoplePerRoom = Math.max(0, Number(this.request?.people || 0));
    const nightlyGuestNames: Array<NightlyGuestNames> = this.nights.controls.map((control) => {
      const night = control as FormGroup;
      const flatNames: Array<string | null> = this.getGuestNames(night).getRawValue();
      const roomSlots = Array.from({ length: roomCount }, (_, roomIndex: number) => ({
        room_number: roomIndex + 1,
        guest_names: flatNames.slice(roomIndex * peoplePerRoom, (roomIndex + 1) * peoplePerRoom),
      }));

      return { date: night.get('date')?.value, room_slots: roomSlots };
    });

    const legacyGuestNames = this.nights.length
      ? this.getGuestNames(this.nights.at(0) as FormGroup).getRawValue()
      : this.getLegacyGuestNames();
    this.modal.close({ guest_names: legacyGuestNames, nightly_guest_names: nightlyGuestNames });
  }

  private buildNightRows(): void {
    const legacyGuestNames = this.getLegacyGuestNames();
    const existingNights = Array.isArray(this.request?.nightly_guest_names) ? this.request.nightly_guest_names : [];
    const existingByDate = new Map<string, Array<string | null>>();

    existingNights.forEach((night: any) => {
      const date = this.toIsoDate(night?.date);
      if (date) {
        existingByDate.set(date, this.flattenNightGuestNames(night));
      }
    });

    this.getNightDates(this.request?.check_in, this.request?.check_out).forEach((date: string) => {
      const existingNames = existingByDate.get(date) || [];
      const names = Array.from(
        { length: this.guestNamesCount },
        (_, index: number) => existingNames[index] ?? legacyGuestNames[index] ?? null
      );
      this.nights.push(this.formBuilder.group({ date: [date], guest_names: this.createGuestNamesArray(names) }));
    });
  }

  private createGuestNamesArray(names: Array<string | null>): FormArray {
    const formArray = new FormArray([]);
    names.forEach((name: string | null) => formArray.push(new FormControl(name)));
    return formArray;
  }

  private getLegacyGuestNames(): Array<string | null> {
    const names = Array.isArray(this.request?.guest_names) ? this.request.guest_names : [];
    return Array.from({ length: this.guestNamesCount }, (_, index: number) => names[index] ?? null);
  }

  private flattenNightGuestNames(night: any): Array<string | null> {
    if (Array.isArray(night?.room_slots)) {
      return night.room_slots.reduce((names: Array<string | null>, room: any) => {
        return names.concat(Array.isArray(room?.guest_names) ? room.guest_names : []);
      }, []);
    }

    return Array.isArray(night?.guest_names) ? night.guest_names : [];
  }

  private getNightDates(checkInValue: any, checkOutValue: any): Array<string> {
    const checkIn = this.parseDate(checkInValue);
    const checkOut = this.parseDate(checkOutValue);

    if (!checkIn || !checkOut || checkIn.getTime() >= checkOut.getTime()) {
      return [];
    }

    const dates: Array<string> = [];
    const cursor = new Date(checkIn.getTime());

    while (cursor.getTime() < checkOut.getTime()) {
      dates.push(this.dateToIso(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    return dates;
  }

  private getRelationLabel(relation: any, selectedId: any): string {
    if (relation && String(relation.id) === String(selectedId)) {
      return relation.value || relation.name || relation.title || relation.label || `#${selectedId}`;
    }

    return selectedId ? `#${selectedId}` : '—';
  }

  private formatDisplayDate(value: any): string {
    const date = this.parseDate(value);
    if (!date) {
      return '—';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  }

  private toIsoDate(value: any): string | null {
    const date = this.parseDate(value);
    return date ? this.dateToIso(date) : null;
  }

  private dateToIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    if (typeof value === 'string') {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
      }

      const displayMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
      if (displayMatch) {
        return new Date(Number(displayMatch[3]), Number(displayMatch[2]) - 1, Number(displayMatch[1]));
      }
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
}
